"use client";

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { subscriptionService, Subscription } from '@/lib/services/subscriptionService';
import { createClient } from '@/lib/supabase/client';

/* ─── Types ─────────────────────────────────────────────────────────────── */
interface Alert {
  id: number;
  type: 'critical' | 'warning' | 'info';
  asset: string;
  category: string;
  message: string;
  time: string;
}

interface ScreeningRow {
  ticker: string;
  name: string;
  phase: 'I' | 'II' | 'III';
  fcfScore: number;
  signal: 'BUY' | 'HOLD' | 'WATCH';
  change24h: string;
  positive: boolean;
}

interface OutlookCard {
  region: string;
  asset: string;
  direction: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  conviction: 'HIGH' | 'MEDIUM' | 'LOW';
  timeframe: string;
  thesis: string;
}

/* ─── Mock Data ──────────────────────────────────────────────────────────── */
const alerts: Alert[] = [
  { id: 1, type: 'critical', asset: 'BTC/USD', category: 'Digital Assets', message: 'Whale accumulation spike: 12,400 BTC moved off exchanges in 4h window. Structural demand signal.', time: '2m ago' },
  { id: 2, type: 'warning', asset: 'EUR/USD', category: 'Forex', message: 'ECB-Fed rate differential compressing. Swap positioning shows extreme EUR short crowding — mean-reversion risk elevated.', time: '18m ago' },
  { id: 3, type: 'info', asset: 'AVGO', category: 'Dividend Equities', message: 'FCF coverage ratio upgraded to 2.4x. Ex-dividend date confirmed: May 14. Payout sustainability score: 91/100.', time: '1h ago' },
  { id: 4, type: 'warning', asset: 'JPY/USD', category: 'Forex', message: 'BoJ normalization signal detected in overnight interbank flows. Asymmetric reversal setup forming.', time: '3h ago' },
  { id: 5, type: 'info', asset: 'ETH On-Chain', category: 'Digital Assets', message: 'DeFi TVL recovery +68% from cycle lows. Active validator count ATH. Developer activity index +34% MoM.', time: '5h ago' },
];

const screeningRows: ScreeningRow[] = [
  { ticker: 'AVGO', name: 'Broadcom Inc.', phase: 'III', fcfScore: 91, signal: 'BUY', change24h: '+1.84%', positive: true },
  { ticker: 'TXN', name: 'Texas Instruments', phase: 'III', fcfScore: 87, signal: 'BUY', change24h: '+0.62%', positive: true },
  { ticker: 'O', name: 'Realty Income Corp.', phase: 'II', fcfScore: 78, signal: 'HOLD', change24h: '-0.31%', positive: false },
  { ticker: 'JNJ', name: 'Johnson & Johnson', phase: 'II', fcfScore: 82, signal: 'HOLD', change24h: '+0.19%', positive: true },
  { ticker: 'MCD', name: "McDonald's Corp.", phase: 'I', fcfScore: 64, signal: 'WATCH', change24h: '-0.55%', positive: false },
  { ticker: 'VZ', name: 'Verizon Communications', phase: 'I', fcfScore: 59, signal: 'WATCH', change24h: '-1.02%', positive: false },
];

const outlookCards: OutlookCard[] = [
  { region: 'North America', asset: 'Dividend Equities', direction: 'BULLISH', conviction: 'HIGH', timeframe: 'Q2–Q3 2026', thesis: 'FCF expansion in AI-adjacent dividend growers; rate plateau supports yield compression.' },
  { region: 'Eurozone', asset: 'EUR/USD', direction: 'BEARISH', conviction: 'HIGH', timeframe: 'Q2 2026', thesis: 'ECB-Fed divergence accelerating. Extreme short crowding creates near-term squeeze risk before resuming downtrend.' },
  { region: 'Japan', asset: 'JPY Carry', direction: 'BEARISH', conviction: 'MEDIUM', timeframe: 'Q2–Q4 2026', thesis: 'BoJ normalization risk asymmetric. Reducing carry exposure recommended ahead of policy signals.' },
  { region: 'Global', asset: 'Bitcoin', direction: 'BULLISH', conviction: 'MEDIUM', timeframe: 'Q2–Q3 2026', thesis: 'Post-halving supply shock + institutional accumulation. On-chain metrics confirm structural demand absorption.' },
  { region: 'Emerging Markets', asset: 'EM FX Basket', direction: 'BULLISH', conviction: 'MEDIUM', timeframe: 'H2 2026', thesis: 'USD peak cycle. BRL, INR, MXN carry setups offer risk-adjusted returns as Fed pivot narrative solidifies.' },
  { region: 'Global', asset: 'REITs', direction: 'BULLISH', conviction: 'LOW', timeframe: 'Q3 2026', thesis: 'UK/US REITs pricing excessive rate risk vs. forward guidance. 15–22% revaluation gap in diversified names.' },
];

const directionColor = { BULLISH: '#4ade80', BEARISH: '#f87171', NEUTRAL: '#fbbf24' };
const convictionBg = { HIGH: 'rgba(74,222,128,0.12)', MEDIUM: 'rgba(251,191,36,0.12)', LOW: 'rgba(248,113,113,0.12)' };
const convictionColor = { HIGH: '#4ade80', MEDIUM: '#fbbf24', LOW: '#f87171' };
const alertBorder = { critical: '#f87171', warning: '#fbbf24', info: '#A3D4FF' };
const alertDot = { critical: '#f87171', warning: '#fbbf24', info: '#4ade80' };

const signalBg: Record<string, string> = { BUY: 'rgba(74,222,128,0.15)', HOLD: 'rgba(163,212,255,0.12)', WATCH: 'rgba(251,191,36,0.12)' };
const signalColor: Record<string, string> = { BUY: '#4ade80', HOLD: '#A3D4FF', WATCH: '#fbbf24' };

const PHASES: Array<'I' | 'II' | 'III'> = ['I', 'II', 'III'];

/* ─── Lock Screen ────────────────────────────────────────────────────────── */
const featureTiles = [
  { icon: '⚡', title: 'Real-Time Alerts', desc: 'Live signals across Digital Assets, Forex, and Dividend Equities with critical/warning/info classification.' },
  { icon: '📊', title: 'Phase Screening Table', desc: 'FCF score bars, signal badges, and 24h change across Phase I / II / III screened equities.' },
  { icon: '🌐', title: 'Quarterly Global Outlook', desc: '6-region macro outlook grid with direction, conviction level, and timeframe metadata.' },
  { icon: '🔒', title: 'Private Advisory Access', desc: 'Direct synthesis sessions with the Research Desk for family offices and UHNW investors.' },
];

function LockScreen() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0A2540', fontFamily: 'var(--font-body)' }}>
      <Header />
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-32 text-center">
        {/* Lock icon */}
        <div className="mb-8 flex items-center justify-center w-20 h-20 rounded-2xl mx-auto"
          style={{ background: 'rgba(163,212,255,0.08)', border: '1px solid rgba(163,212,255,0.2)' }}>
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
            <rect x="7" y="16" width="22" height="15" rx="3" stroke="#A3D4FF" strokeWidth="2"/>
            <path d="M12 16V11a6 6 0 0 1 12 0v5" stroke="#A3D4FF" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="18" cy="23" r="2" fill="#A3D4FF"/>
          </svg>
        </div>

        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-mono mb-6"
          style={{ background: 'rgba(163,212,255,0.08)', border: '1px solid rgba(163,212,255,0.2)', color: '#A3D4FF', letterSpacing: '0.15em' }}>
          <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#f87171' }} />
          SUBSCRIBER ACCESS REQUIRED
        </div>

        <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4 leading-tight"
          style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}>
          Research Intelligence Terminal
        </h1>
        <p className="text-lg mb-10 max-w-xl mx-auto" style={{ color: '#E3F2FD', opacity: 0.65 }}>
          Institutional-grade live signals, multi-phase equity screening, and global macro outlook — exclusively for paid subscribers.
        </p>

        {/* Feature preview tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl w-full mb-10">
          {featureTiles.map((tile) => (
            <div key={tile.title} className="text-left p-5 rounded-xl"
              style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
              <div className="text-2xl mb-3">{tile.icon}</div>
              <h3 className="font-semibold text-white mb-1 text-sm">{tile.title}</h3>
              <p className="text-xs leading-relaxed" style={{ color: '#E3F2FD', opacity: 0.55 }}>{tile.desc}</p>
            </div>
          ))}
        </div>

        <Link href="/homepage#pricing"
          className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-bold text-sm transition-all duration-200"
          style={{ background: '#A3D4FF', color: '#0A2540', letterSpacing: '0.03em' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; (e.currentTarget as HTMLAnchorElement).style.color = '#A3D4FF'; (e.currentTarget as HTMLAnchorElement).style.border = '2px solid #A3D4FF'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = '#A3D4FF'; (e.currentTarget as HTMLAnchorElement).style.color = '#0A2540'; (e.currentTarget as HTMLAnchorElement).style.border = '2px solid transparent'; }}>
          Unlock Full Access →
        </Link>
        <p className="text-xs mt-4" style={{ color: '#E3F2FD', opacity: 0.35 }}>
          Already a subscriber? <Link href="/dashboard" className="underline" style={{ color: '#A3D4FF' }}>Log in to your dashboard</Link>
        </p>
      </main>
      <Footer />
    </div>
  );
}

/* ─── Full Terminal ──────────────────────────────────────────────────────── */
function IntelligenceTerminal() {
  const [activePhase, setActivePhase] = useState<'I' | 'II' | 'III'>('III');

  const filteredRows = screeningRows.filter((r) => r.phase === activePhase);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0A2540', fontFamily: 'var(--font-body)' }}>
      <Header />
      <main className="flex-1 pt-24 pb-20 px-6 md:px-12" style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>

        {/* Page header */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-mono mb-6"
            style={{ background: 'rgba(163,212,255,0.08)', border: '1px solid rgba(163,212,255,0.2)', color: '#A3D4FF', letterSpacing: '0.15em' }}>
            <span className="w-1.5 h-1.5 rounded-full inline-block animate-pulse" style={{ background: '#4ade80' }} />
            LIVE · RESEARCH INTELLIGENCE TERMINAL
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight mb-3"
            style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}>
            Intelligence Terminal
          </h1>
          <p className="text-base" style={{ color: '#E3F2FD', opacity: 0.6 }}>
            Real-time signals, multi-phase screening, and global macro outlook — updated continuously.
          </p>
        </div>

        {/* ── Real-Time Alerts ── */}
        <section className="mb-14">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-2 h-2 rounded-full inline-block animate-pulse" style={{ background: '#4ade80' }} />
            <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-display)' }}>Real-Time Alerts</h2>
            <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(74,222,128,0.12)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.2)' }}>LIVE</span>
          </div>
          <div className="space-y-3">
            {alerts.map((alert) => (
              <div key={alert.id} className="flex items-start gap-4 p-4 rounded-xl"
                style={{ background: '#1A2A44', border: `1px solid ${alertBorder[alert.type]}33` }}>
                <div className="flex-shrink-0 mt-1">
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: alertDot[alert.type] }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-bold text-white">{alert.asset}</span>
                    <span className="text-xs font-mono px-2 py-0.5 rounded"
                      style={{ background: 'rgba(163,212,255,0.08)', color: '#A3D4FF', border: '1px solid rgba(163,212,255,0.15)' }}>
                      {alert.category}
                    </span>
                    <span className="text-xs font-mono uppercase tracking-widest"
                      style={{ color: alertDot[alert.type], opacity: 0.85 }}>
                      {alert.type}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: '#E3F2FD', opacity: 0.7 }}>{alert.message}</p>
                </div>
                <span className="text-xs font-mono flex-shrink-0" style={{ color: '#E3F2FD', opacity: 0.35 }}>{alert.time}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Divider */}
        <div className="mb-14" style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #0F4C81 30%, #0F4C81 70%, transparent)' }} />

        {/* ── Phase Screening Table ── */}
        <section className="mb-14">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-display)' }}>Dividend Equity Screening</h2>
            <div className="flex items-center gap-2">
              {PHASES.map((p) => (
                <button key={p} onClick={() => setActivePhase(p)}
                  className="text-xs font-mono px-3 py-1.5 rounded-lg transition-all duration-200"
                  style={{
                    background: activePhase === p ? '#A3D4FF' : 'rgba(163,212,255,0.08)',
                    color: activePhase === p ? '#0A2540' : '#A3D4FF',
                    border: `1px solid ${activePhase === p ? '#A3D4FF' : 'rgba(163,212,255,0.2)'}`,
                    fontFamily: 'var(--font-mono)',
                  }}>
                  Phase {p}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid #1E3A5F' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#1A2A44', borderBottom: '1px solid #1E3A5F' }}>
                  {['Ticker', 'Company', 'FCF Score', 'Signal', '24h Change'].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-mono uppercase tracking-widest"
                      style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.12em' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, i) => (
                  <tr key={row.ticker}
                    style={{ background: i % 2 === 0 ? 'rgba(26,42,68,0.5)' : 'transparent', borderBottom: '1px solid rgba(30,58,95,0.5)' }}>
                    <td className="px-5 py-4 font-mono font-bold text-white">{row.ticker}</td>
                    <td className="px-5 py-4" style={{ color: '#E3F2FD', opacity: 0.75 }}>{row.name}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-24 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(163,212,255,0.1)' }}>
                          <div className="h-full rounded-full" style={{ width: `${row.fcfScore}%`, background: 'linear-gradient(90deg, #4A90E2, #A3D4FF)' }} />
                        </div>
                        <span className="text-xs font-mono" style={{ color: '#A3D4FF' }}>{row.fcfScore}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-xs font-mono font-bold px-2.5 py-1 rounded"
                        style={{ background: signalBg[row.signal], color: signalColor[row.signal], border: `1px solid ${signalColor[row.signal]}33` }}>
                        {row.signal}
                      </span>
                    </td>
                    <td className="px-5 py-4 font-mono text-sm font-semibold"
                      style={{ color: row.positive ? '#4ade80' : '#f87171' }}>
                      {row.change24h}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Divider */}
        <div className="mb-14" style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #0F4C81 30%, #0F4C81 70%, transparent)' }} />

        {/* ── Quarterly Global Outlook ── */}
        <section>
          <h2 className="text-lg font-bold text-white mb-6" style={{ fontFamily: 'var(--font-display)' }}>Quarterly Global Outlook</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {outlookCards.map((card) => (
              <div key={card.region + card.asset} className="p-5 rounded-xl flex flex-col gap-3"
                style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-widest mb-0.5" style={{ color: '#A3D4FF', opacity: 0.7 }}>{card.region}</p>
                    <p className="text-sm font-bold text-white">{card.asset}</p>
                  </div>
                  <span className="text-xs font-mono font-bold px-2 py-0.5 rounded flex-shrink-0"
                    style={{ color: directionColor[card.direction], background: `${directionColor[card.direction]}18`, border: `1px solid ${directionColor[card.direction]}33` }}>
                    {card.direction}
                  </span>
                </div>
                <p className="text-xs leading-relaxed flex-1" style={{ color: '#E3F2FD', opacity: 0.6 }}>{card.thesis}</p>
                <div className="flex items-center justify-between pt-2" style={{ borderTop: '1px solid rgba(30,58,95,0.6)' }}>
                  <span className="text-xs font-mono px-2 py-0.5 rounded"
                    style={{ background: convictionBg[card.conviction], color: convictionColor[card.conviction] }}>
                    {card.conviction} CONVICTION
                  </span>
                  <span className="text-xs font-mono" style={{ color: '#E3F2FD', opacity: 0.4 }}>{card.timeframe}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

/* ─── Page Entry ─────────────────────────────────────────────────────────── */
export default function IntelligencePage() {
  const [loading, setLoading] = useState(true);
  const [isSubscriber, setIsSubscriber] = useState(false);

  useEffect(() => {
    async function checkAccess() {
      try {
        const supabase = createClient();
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }
        const sub: Subscription | null = await subscriptionService.getSubscription();
        setIsSubscriber(subscriptionService.isActive(sub));
      } catch {
        setIsSubscriber(false);
      } finally {
        setLoading(false);
      }
    }
    checkAccess();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A2540' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#A3D4FF', borderTopColor: 'transparent' }} />
          <span className="text-xs font-mono" style={{ color: '#A3D4FF', opacity: 0.6 }}>AUTHENTICATING…</span>
        </div>
      </div>
    );
  }

  return isSubscriber ? <IntelligenceTerminal /> : <LockScreen />;
}
